import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleString('es', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

/**
 * Chat 1-a-1 entre el usuario actual y `peer`.
 * peer = { id, name, role, email }
 * currentUserId = id del usuario actual (para alinear los bubbles)
 */
const ChatPanel = ({ peer, currentUserId, onMessageSent }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState('');
  const scrollEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    if (!peer?.id) return;
    try {
      const r = await axios.get(`${API}/chat/conversation/${peer.id}`);
      setMessages(r.data.messages || []);
      // marcar como leídos los recibidos
      try {
        await axios.post(`${API}/chat/mark-read/${peer.id}`);
        if (onMessageSent) onMessageSent();
      } catch {
        // silencioso
      }
    } catch (e) {
      console.error('Chat fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [peer?.id, onMessageSent]);

  useEffect(() => {
    setLoading(true);
    fetchMessages();
    // poll cada 8s
    pollRef.current = setInterval(fetchMessages, 8000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchMessages]);

  useEffect(() => {
    // scroll al final al añadir mensajes
    if (scrollEndRef.current) {
      scrollEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  const handleSend = async () => {
    const text = content.trim();
    if (!text) return;
    if (!peer?.id) {
      toast.error('No hay destinatario disponible');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/chat/send`, {
        recipient_id: peer.id,
        content: text
      });
      setMessages((prev) => [...prev, r.data]);
      setContent('');
      if (onMessageSent) onMessageSent();
    } catch (e) {
      // Detección de feature locked (HTTP 403 con error=feature_locked)
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'feature_locked') {
        toast.error(detail.message || 'Necesitas un plan Pro para escribir mensajes', {
          duration: 8000,
          action: {
            label: 'Ver planes',
            onClick: () => { window.location.href = '/pricing'; }
          }
        });
      } else {
        toast.error(detail?.message || detail || 'Error al enviar el mensaje');
      }
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!peer) {
    return (
      <div className="flex items-center justify-center h-full p-8 text-muted-foreground">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Selecciona una conversación</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-zinc-900/40 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
          {(peer.name || peer.email || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-foreground" data-testid="chat-peer-name">{peer.name || peer.email}</p>
          <p className="text-xs text-muted-foreground capitalize">
            {peer.role === 'trainer' ? 'Tu entrenador' : peer.role === 'admin' ? 'Soporte GuardiansFit' : 'Cliente'}
          </p>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 bg-background/40" data-testid="chat-messages-container">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 text-sm">
            Aún no hay mensajes. ¡Saluda para empezar la conversación!
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m) => {
              const mine = m.sender_id === currentUserId;
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                  data-testid={`chat-msg-${mine ? 'mine' : 'theirs'}`}
                >
                  <div
                    className={`
                      max-w-[78%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap break-words
                      ${mine
                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                        : 'bg-zinc-800 text-foreground rounded-bl-sm'}
                    `}
                  >
                    <div>{m.content}</div>
                    <div className={`text-[10px] mt-1 opacity-70 ${mine ? 'text-right' : 'text-left'}`}>
                      {formatTime(m.sent_at)}
                      {mine && m.read ? ' · leído' : ''}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={scrollEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Composer */}
      <div className="p-3 border-t border-border bg-zinc-900/40">
        <div className="flex gap-2 items-end">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Escribe un mensaje…"
            rows={2}
            className="flex-1 resize-none bg-background"
            data-testid="chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={sending || !content.trim()}
            className="shrink-0"
            data-testid="chat-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Enter para enviar · Shift+Enter para nueva línea</p>
      </div>
    </div>
  );
};

export default ChatPanel;
